import clsx from "clsx";
import { forwardRef } from "react";

interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  unit?: string;
  error?: string;
  hint?: string;
  optional?: boolean;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ label, unit, error, hint, optional, id, className, ...props }, ref) => {
    const inputId = id ?? label.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className={clsx("mb-4", className)}>
        <label htmlFor={inputId} className={clsx("block text-sm font-medium mb-1", optional ? "text-neko-gray" : "text-neko-gray-light")}>
          {label}{optional && <span className="text-neko-gray font-normal"> (optional)</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="number"
            inputMode="numeric"
            className={clsx(
              "w-full border rounded-lg px-3 py-2.5 text-neko-white text-base",
              "placeholder-neko-gray focus:outline-none focus:ring-2 transition-all",
              optional ? "bg-neko-dark-2/70" : "bg-neko-dark-2",
              unit ? "pr-16" : "pr-3",
              error
                ? "border-neko-red focus:ring-neko-red/40"
                : optional
                  ? "border-neko-dark-3/60 focus:ring-neko-orange/40 focus:border-neko-orange"
                  : "border-neko-dark-3 focus:ring-neko-orange/40 focus:border-neko-orange"
            )}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          {unit && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neko-gray text-sm pointer-events-none">
              {unit}
            </span>
          )}
        </div>
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-neko-gray mt-1">{hint}</p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-neko-red mt-1" role="alert">{error}</p>
        )}
      </div>
    );
  }
);

NumberInput.displayName = "NumberInput";
export default NumberInput;
